import React from "react";
import ShowWithSkeleton from "src/utils/show-with-skeleton";
import executiveValueFormatter from "src/scenes/overview/overview-components/executive-value-formatter";
import styled from "styled-components";
import { Box, Paper, Stack } from "@mui/material";
import ReactCardFlip from "react-card-flip";
import calculateDependencies from "src/scenes/overview/overview-components/calculate-dependencies";
import AccuracyLinear from "src/scenes/overview/overview-components/accuracy-linear";
import { docsPathConstants, getDocsPath } from "src/utils/docs-path-constants";
import { useOrg } from "src/components/providers/org-provider";
import ExternalLink from "../../../../components/external-link";

const CustomPaper = styled(Paper)`
    width: 100%;
    padding: 16px;
    border-radius: 14px 14px 0 0;
`;

const MetricHeaderText = styled.p`
    margin: 12px 0 12px 0;
    font-size: 15px;
    font-weight: 600;
`;

const MetricBreakdownText = styled.p`
    margin: 0;
    font-size: 12px;
    font-weight: 300;
`;

const InfoText = styled.p`
    margin: 0;
    font-size: 12px;
    font-weight: 200;
`;

const BreakdownContainer = styled(Box)`
    padding: 12px;
    border-radius: 12px;
    background: #393939;
    margin-top: 12px;
`;

const DeliverySectionOutput = ({ data, fetchAction, expandedView, isFlipped }) => {
    const { org } = useOrg();
    return (
        <ReactCardFlip isFlipped={isFlipped} containerStyle={{ width: "100%", height: "100%" }}>
            <CustomPaper>
                {!isFlipped && (
                    <>
                        <MetricHeaderText style={{ margin: 0, fontWeight: 200, opacity: 0.5 }}>
                            Output
                        </MetricHeaderText>
                        <Stack direction="column" mb={2}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <MetricHeaderText>Throughput</MetricHeaderText>
                                <AccuracyLinear
                                    data={
                                        data &&
                                        data.throughput &&
                                        data.throughput.meta &&
                                        data.throughput.meta.accuracy
                                    }
                                />
                            </Stack>
                            <ShowWithSkeleton
                                fetchAction={fetchAction}
                                height={50}
                                width="100%"
                                streamingProperty={
                                    fetchAction && fetchAction.data && fetchAction.data.throughput
                                }
                            >
                                <Stack direction="row">
                                    {executiveValueFormatter(
                                        data &&
                                            data.throughput &&
                                            data.throughput.breakdown.throughputGit,
                                        "custom",
                                        false,
                                        "PRs merged",
                                        data &&
                                            data.throughput &&
                                            data.throughput.stats &&
                                            data.throughput.stats.throughputGitSlope
                                    )}{" "}
                                    {executiveValueFormatter(
                                        data &&
                                            data.throughput &&
                                            data.throughput.breakdown.throughputPm,
                                        "custom",
                                        false,
                                        "Issues Done",
                                        data &&
                                            data.throughput &&
                                            data.throughput.stats &&
                                            data.throughput.stats.throughputPmSlope
                                    )}
                                </Stack>
                            </ShowWithSkeleton>
                            {expandedView && (
                                <BreakdownContainer>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <MetricBreakdownText>
                                            Average PRs merged:
                                        </MetricBreakdownText>
                                        <ShowWithSkeleton
                                            fetchAction={fetchAction}
                                            streamingProperty={
                                                fetchAction &&
                                                fetchAction.data &&
                                                fetchAction.data.throughput &&
                                                fetchAction.data.throughput.breakdown
                                            }
                                        >
                                            {executiveValueFormatter(
                                                data &&
                                                    data.throughput &&
                                                    data.throughput.breakdown
                                                        .throughputGitPerUserAvg,
                                                "custom",
                                                true,
                                                "per user"
                                            )}
                                        </ShowWithSkeleton>
                                    </Stack>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <MetricBreakdownText>
                                            Median PRs merged:
                                        </MetricBreakdownText>
                                        <ShowWithSkeleton
                                            fetchAction={fetchAction}
                                            streamingProperty={
                                                fetchAction &&
                                                fetchAction.data &&
                                                fetchAction.data.throughput &&
                                                fetchAction.data.throughput.breakdown
                                            }
                                        >
                                            {executiveValueFormatter(
                                                data &&
                                                    data.throughput &&
                                                    data.throughput.breakdown
                                                        .throughputGitPerUserMedian,
                                                "custom",
                                                true,
                                                "per user"
                                            )}
                                        </ShowWithSkeleton>
                                    </Stack>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <MetricBreakdownText>
                                            Average Issues Done:
                                        </MetricBreakdownText>
                                        <ShowWithSkeleton
                                            fetchAction={fetchAction}
                                            streamingProperty={
                                                fetchAction &&
                                                fetchAction.data &&
                                                fetchAction.data.throughput &&
                                                fetchAction.data.throughput.breakdown
                                            }
                                        >
                                            {executiveValueFormatter(
                                                data &&
                                                    data.throughput &&
                                                    data.throughput.breakdown
                                                        .throughputPmPerUserAvg,
                                                "custom",
                                                true,
                                                "per user"
                                            )}
                                        </ShowWithSkeleton>
                                    </Stack>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <MetricBreakdownText>
                                            Median Issues Done:
                                        </MetricBreakdownText>
                                        <ShowWithSkeleton
                                            fetchAction={fetchAction}
                                            streamingProperty={
                                                fetchAction &&
                                                fetchAction.data &&
                                                fetchAction.data.throughput &&
                                                fetchAction.data.throughput.breakdown
                                            }
                                        >
                                            {executiveValueFormatter(
                                                data &&
                                                    data.throughput &&
                                                    data.throughput.breakdown
                                                        .throughputPmPerUserMedian,
                                                "custom",
                                                true,
                                                "per user"
                                            )}
                                        </ShowWithSkeleton>
                                    </Stack>
                                </BreakdownContainer>
                            )}
                        </Stack>
                        <Stack direction="column" mb={1}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <MetricHeaderText>Lead Time</MetricHeaderText>
                                <AccuracyLinear
                                    data={
                                        data &&
                                        data.leadTime &&
                                        data.leadTime.meta &&
                                        data.leadTime.meta.accuracy
                                    }
                                />
                            </Stack>
                            <ShowWithSkeleton
                                fetchAction={fetchAction}
                                height={50}
                                width="100%"
                                streamingProperty={
                                    fetchAction && fetchAction.data && fetchAction.data.leadTime
                                }
                            >
                                <Stack direction="row">
                                    {executiveValueFormatter(
                                        data &&
                                            data.leadTime &&
                                            data.leadTime.breakdown &&
                                            data.leadTime.breakdown.leadTimeGitAdj,
                                        "time",
                                        false,
                                        <>
                                            <br />
                                            (PRs Merged - no outliers)
                                        </>,
                                        data &&
                                            data.leadTime &&
                                            data.leadTime.stats &&
                                            data.leadTime.stats.leadTimeSlopeGit
                                    )}{" "}
                                    {executiveValueFormatter(
                                        data &&
                                            data.leadTime &&
                                            data.leadTime.breakdown.leadTimePmAdj,
                                        "time",
                                        false,
                                        <>
                                            <br />
                                            (Issues Done - no outliers)
                                        </>,
                                        data &&
                                            data.leadTime &&
                                            data.leadTime.stats &&
                                            data.leadTime.stats.leadTimeSlopePm
                                    )}
                                </Stack>
                            </ShowWithSkeleton>
                            {expandedView && (
                                <BreakdownContainer>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <MetricBreakdownText>
                                            PR Lead Time Mean (+outliers):
                                        </MetricBreakdownText>
                                        <ShowWithSkeleton
                                            fetchAction={fetchAction}
                                            streamingProperty={
                                                fetchAction &&
                                                fetchAction.data &&
                                                fetchAction.data.leadTime &&
                                                fetchAction.data.leadTime.breakdown
                                            }
                                        >
                                            {executiveValueFormatter(
                                                data &&
                                                    data.leadTime &&
                                                    data.leadTime.breakdown.leadTimeGit,
                                                "time",
                                                true
                                            )}
                                        </ShowWithSkeleton>
                                    </Stack>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <MetricBreakdownText>
                                            PR Lead Time median:
                                        </MetricBreakdownText>
                                        <ShowWithSkeleton
                                            fetchAction={fetchAction}
                                            streamingProperty={
                                                fetchAction &&
                                                fetchAction.data &&
                                                fetchAction.data.leadTime &&
                                                fetchAction.data.leadTime.breakdown
                                            }
                                        >
                                            {executiveValueFormatter(
                                                data &&
                                                    data.leadTime &&
                                                    data.leadTime.breakdown.leadTimeGitMedian,
                                                "time",
                                                true
                                            )}
                                        </ShowWithSkeleton>
                                    </Stack>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <MetricBreakdownText>
                                            PR Lead Time 95th percentile:
                                        </MetricBreakdownText>
                                        <ShowWithSkeleton
                                            fetchAction={fetchAction}
                                            streamingProperty={
                                                fetchAction &&
                                                fetchAction.data &&
                                                fetchAction.data.leadTime &&
                                                fetchAction.data.leadTime.breakdown
                                            }
                                        >
                                            {executiveValueFormatter(
                                                data &&
                                                    data.leadTime &&
                                                    data.leadTime.breakdown.leadTimeGit95th,
                                                "time",
                                                true
                                            )}
                                        </ShowWithSkeleton>
                                    </Stack>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <MetricBreakdownText>
                                            Issues Lead Time Mean (+outliers):
                                        </MetricBreakdownText>
                                        <ShowWithSkeleton
                                            fetchAction={fetchAction}
                                            streamingProperty={
                                                fetchAction &&
                                                fetchAction.data &&
                                                fetchAction.data.leadTime &&
                                                fetchAction.data.leadTime.breakdown
                                            }
                                        >
                                            {executiveValueFormatter(
                                                data &&
                                                    data.leadTime &&
                                                    data.leadTime.breakdown.leadTimePm,
                                                "time",
                                                true
                                            )}
                                        </ShowWithSkeleton>
                                    </Stack>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <MetricBreakdownText>
                                            Issues Lead Time median:
                                        </MetricBreakdownText>
                                        <ShowWithSkeleton
                                            fetchAction={fetchAction}
                                            streamingProperty={
                                                fetchAction &&
                                                fetchAction.data &&
                                                fetchAction.data.leadTime &&
                                                fetchAction.data.leadTime.breakdown
                                            }
                                        >
                                            {executiveValueFormatter(
                                                data &&
                                                    data.leadTime &&
                                                    data.leadTime.breakdown.leadTimePmMedian,
                                                "time",
                                                true
                                            )}
                                        </ShowWithSkeleton>
                                    </Stack>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <MetricBreakdownText>
                                            Issues Lead Time 95th percentile:
                                        </MetricBreakdownText>
                                        <ShowWithSkeleton
                                            fetchAction={fetchAction}
                                            streamingProperty={
                                                fetchAction &&
                                                fetchAction.data &&
                                                fetchAction.data.leadTime &&
                                                fetchAction.data.leadTime.breakdown
                                            }
                                        >
                                            {executiveValueFormatter(
                                                data &&
                                                    data.leadTime &&
                                                    data.leadTime.breakdown.leadTimePm95th,
                                                "time",
                                                true
                                            )}
                                        </ShowWithSkeleton>
                                    </Stack>
                                </BreakdownContainer>
                            )}
                        </Stack>
                    </>
                )}
            </CustomPaper>
            <CustomPaper>
                {isFlipped && (
                    <>
                        <MetricHeaderText
                            style={{ margin: "0 0 10px 0", fontWeight: 200, opacity: 0.5 }}
                        >
                            Output
                        </MetricHeaderText>
                        <InfoText>Metrics indicating the amount of end-result produced.</InfoText>
                        <Stack direction="column" mb={2}>
                            <MetricHeaderText>
                                <ExternalLink
                                    href={getDocsPath(
                                        org,
                                        docsPathConstants.deliveryThroughputDefinition
                                    )}
                                    underline="none"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Throughput
                                </ExternalLink>
                            </MetricHeaderText>
                            <InfoText>
                                Developer output produced, measured in issues completed or Pull
                                Requests merged. An indication of the delivery of work.
                            </InfoText>
                            <Stack
                                direction="column"
                                alignItems="left"
                                spacing={1}
                                mt={1}
                                style={{ width: "100%" }}
                            >
                                <InfoText>Data source impact:</InfoText>
                                {calculateDependencies(
                                    data &&
                                        data.throughput &&
                                        data.throughput.meta.accuracy.dependencies
                                )}
                            </Stack>
                        </Stack>
                        <Stack direction="column" mb={2}>
                            <MetricHeaderText>
                                <ExternalLink
                                    href={getDocsPath(
                                        org,
                                        docsPathConstants.deliveryLeadTimeDefinition
                                    )}
                                    underline="none"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Lead Time
                                </ExternalLink>
                            </MetricHeaderText>
                            <InfoText>
                                Unit of work completion rate, measured in lead time for
                                (Jira/Notion) issues, or Pull Requests.
                            </InfoText>
                            <Stack
                                direction="column"
                                alignItems="left"
                                spacing={1}
                                mt={1}
                                style={{ width: "100%" }}
                            >
                                <InfoText>Data source impact:</InfoText>
                                {calculateDependencies(
                                    data &&
                                        data.leadTime &&
                                        data.leadTime.meta.accuracy.dependencies
                                )}
                            </Stack>
                        </Stack>
                    </>
                )}
            </CustomPaper>
        </ReactCardFlip>
    );
};

export default DeliverySectionOutput;
